import React, { useState, useCallback } from 'react';
import useRequest from 'src/api/useRequest';
import { patentRightsServiceProvider } from 'src/api/service/patentRightsService';
import useService from 'src/hooks/useService';
import QueryString from 'qs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from 'src/hooks/useStore';

import SearchResult from './SearchResult';
import ApplClassTag from '../../../layout/tag/ApplClassTag';
import ResultLoading from 'src/components/ui-kit/ResultLoading';
import { tryParseInt } from 'src/utils/formatters';
import { rowComponents } from 'src/utils/indexs';

import { FormType, globalComponents } from 'src/types/globalTypes';
import PatentResultMobile from './PatentResultMobile';
import { getCookie, setCookie } from 'typescript-cookie'
import { useEffect, useRef } from 'react';

import {
  ApplyClass,
  IReqPatentRightsPage,
  IResPatentRightsPage,
  PatentOrderOptions,
} from 'src/types/api/patentRightsApiTypes';
import {
  Table,
  Th,
  Td,
  ListWrapper,
  ListRow,
  TypeItem,
  ListContent,
  ListItem,
  ListTitle,
  ListItemContent,
} from 'src/components/contents/search/result/SearchResult';
import PatentSearchTip from './PatentSearchTip';
import styled from 'styled-components/macro';

export const Thead = styled.div<{ bgcolor: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 2fr 2fr 1fr 1fr;
  background-color: ${(prop) => prop.bgcolor};
  border-bottom: 1px solid ${(prop) => prop.bgcolor};
  align-items: center;
  justify-items: center;
  border-radius: 8px 8px 0px 0px;
`;
const Tr = styled.div<{ bgcolor: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 2fr 2fr 1fr 1fr;
  width: 100%;
  border-bottom: 1px solid ${(prop) => prop.bgcolor};
  cursor: pointer;
  :hover {
    background-color: ${(prop) => prop.bgcolor};
  }
`;

/** 申請類別轉換代碼。 */
export const changeNumToApplClass = (applyClass: string) => {
  switch (applyClass) {
    case '1':
      return ApplyClass.I;
    case '2':
      return ApplyClass.M;
    case '3':
      return ApplyClass.D;
    default:
      return;
  }
};

const patentRightsService = patentRightsServiceProvider();

/**
 * 專利搜尋結果頁面
 */
const PatentResult: React.FC = () => {
  const path = useService();
  const navigate = useNavigate();
  const isMobile = useStore((state) => state.isMobile);
  const { search } = useLocation();
  const params = QueryString.parse(search, { ignoreQueryPrefix: true });

  // 表格常數
  const SEPRATOR = '，'; //名稱間隔
  const component = globalComponents.P;
  const linkColor = component.color.seventh;

  const [formType, setFormType] = useState<FormType>(getCookie('formType') == null || getCookie('formType') === FormType.list ? FormType.list : FormType.table);
  const [pageNo, setPageNo] = useState<number>(tryParseInt(params.pageNo));
  const [pageSize, setPageSize] = useState<number>(getCookie('pageSize') == null ? tryParseInt(params.pageSize) : getCookie('pageSize'));
  const [orderCondition, setOrderCondition] = useState<string | number>(
    params.orderBy?.toString() || PatentOrderOptions[0].value,
  );

  // 將搜尋條件放入state
  const [reqParams, setReqParams] = useState<IReqPatentRightsPage>({
    ...params,
    pageNo: typeof tryParseInt(params.pageNo) === 'number' ? tryParseInt(params.pageNo) : 1,
  });

  // 搜尋條件
  const tip = <PatentSearchTip reqParams={reqParams} />;

  /** 跳轉至案件明細頁面 */
  const handleOnClick = (applNo: string, applyClass: string) => {
    navigate(process.env.PUBLIC_URL + '/patentRights/detail', {
      state: {
        applNo: applNo,
        applyClass: applyClass,
      },
    });
  };
  
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>, 
    callback: () => void
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!isFirstRender.current) {
      setReqParams(prevParams => ({
        ...prevParams,
        pageNo,
        pageSize,
        orderBy: orderCondition
      }));
    }
  }, [pageNo, pageSize, orderCondition, setReqParams]);

  const [isLoading, , res] = useRequest<IResPatentRightsPage>(
    useCallback(() => {
      // 執行原本的函數
      return patentRightsService.getPatentRights(reqParams);
    }, [reqParams]),
  );

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const tableForm = res?.data && (
    <Table>
      <Thead bgcolor={component.color.third}>
        <Th>專利類型</Th>
        <Th>證書號數</Th>
        <Th>申請案號</Th>
        <Th>專利名稱</Th>
        <Th>發明人/申請人</Th>
        <Th>申請日期</Th>
        <Th>年費有效日期</Th>
      </Thead>
      {res.data.map((item, index) => {
        index += 1;
        return (
          <Tr
            key={index}
            bgcolor={component.color.third}
            onClick={() => handleOnClick(item.applNo, item.applyClass)}
			tabIndex={0}
			role="button"
			onKeyDown={(e) => handleKeyDown(e, () => handleOnClick(item.applNo, item.applyClass))}
          >
            <Td>
              <ApplClassTag type={item.applyClass} />
            </Td>
            <Td>{item.patentNo}</Td>
            <Td>{item.applNo}</Td>
            <Td style={{ color: linkColor }}>{item.patentName}</Td>
            <Td>
              {rowComponents(item.inventorNameC, SEPRATOR)}
              /<br />
              {rowComponents(item.applNameC, SEPRATOR)}
            </Td>
            <Td>{item.applyDate}</Td>
            <Td>{item.chargeExpiredDate}</Td>
          </Tr>
        );
      })}
    </Table>
  );

  const listForm = res?.data && (
    <ListWrapper>
      {res.data.map((item, index) => {
        index += 1;
        return (
          <ListRow
            key={index}
            bgcolor={component.color.third}
            onClick={() => handleOnClick(item.applNo, item.applyClass)}
			tabIndex={0}
			role="button"
			onKeyDown={(e) => handleKeyDown(e, () => handleOnClick(item.applNo, item.applyClass))}
          >
            <TypeItem>
              <ApplClassTag type={item.applyClass} />
            </TypeItem>
            <ListContent>
              <ListItem>
                <ListTitle>證書號數/申請案號：</ListTitle>
                <ListItemContent>
                  {item.patentNo}/{item.applNo}
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListTitle>發明人/申請人：</ListTitle>
                <ListItemContent>
                  {rowComponents(item.inventorNameC, SEPRATOR)}/
                  {rowComponents(item.applNameC, SEPRATOR)}
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListTitle>專利名稱：</ListTitle>
                <ListItemContent style={{ color: linkColor }}>{item.patentName}</ListItemContent>
              </ListItem>
            </ListContent>
            <ListContent>
              <ListItem>
                <ListTitle>申請日期：</ListTitle>
                <ListItemContent>{item.applyDate}</ListItemContent>
              </ListItem>
              <ListItem>
                <ListTitle>年費有效日期：</ListTitle>
                <ListItemContent>{item.chargeExpiredDate}</ListItemContent>
              </ListItem>
            </ListContent>
          </ListRow>
        );
      })}
    </ListWrapper>
  );

  const mobileForm = res?.data && (
    <PatentResultMobile data={res.data} colorType={component.color} handleOnClick={handleOnClick} />
  );

  const desktopForm = formType === FormType.list ? listForm : tableForm;
  setCookie('formType', formType, { secure: true });
  const showForm = isMobile ? mobileForm : desktopForm;

  return (
    <SearchResult
      title={component.title}
      bannerBgImg={isMobile ? component.bgImgBannerMobile : component.bgImgBanner}
      image={component.hoverImage}
      imgDot={component.bgImgDot}
      bgColor={component.color}
      returnPageUrl={path}
      tip={tip}
      formType={formType}
      setFormType={setFormType}
      page={res?.page}
      pageNo={pageNo}
      setPageNo={setPageNo}
      pageSize={pageSize}
      setPageSize={setPageSize}
      orderOptions={PatentOrderOptions}
      orderCondition={orderCondition}
      setOrderCondition={setOrderCondition}
      reqParams={reqParams}
    >
      {isLoading ? <ResultLoading color={component.color.second} /> : showForm}
    </SearchResult>
  );
};

export default PatentResult;
